import React, { useState, useRef, useEffect } from 'react';
import { FaTrash } from 'react-icons/fa';
import { Form, Input, InputNumber, Select, Switch, message } from 'antd';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useAuth } from '../../contexts/AppContext';
import ImageUploader from '../utils/ImageUploader';
import { buildQuillModules, QUILL_TEXT_FORMATS } from '../utils/quillConfig';
import { RECOMMENDATION_CATEGORIES } from '../../constants/recommendationCategories';
import { IMPORTED_EVENT_CATEGORIES } from '../../constants/importedEventCategories';

function isEventsSectionName(name) {
    return typeof name === 'string' && name.trim().toLowerCase() === 'events';
}

function isQuillContentEmpty(html) {
    if (!html) return true;
    return html.replace(/<(.|\n)*?>/g, '').trim().length === 0;
}

function normalizeImageUrl(value) {
    if (!value) return '';

    if (Array.isArray(value)) return value[0] || '';

    if (typeof value === 'object') {
        if (typeof value.url === 'string') return value.url;
        return '';
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
            try {
                return normalizeImageUrl(JSON.parse(trimmed));
            } catch {
                return trimmed;
            }
        }
        return trimmed;
    }

    return '';
}

const GOOGLE_GEOCODING_KEY = 'AIzaSyD63grvHYISPK4dLfyj5kKmC6MR_wQPJ80';

const TsLeafNode = ({
    detail,
    setDetail,
    handleInputChange,
    handleImageDelete,
    isNearbyLocationEntry = false,
}) => {
    const [showBanner, setShowBanner] = useState((detail.searchTags || '').includes('logo'));
    const [insertPanelOpen, setInsertPanelOpen] = useState(false);
    const [ratingValue, setRatingValue] = useState(5);
    const [ratingStyle, setRatingStyle] = useState('stars');
    const [isLookingUpAddress, setIsLookingUpAddress] = useState(false);
    const reactQuillRef = useRef();
    const [form] = Form.useForm();
    const { token, selectedRole, userInfo } = useAuth();
    const id = selectedRole?.id || '';
    const prefix = userInfo?.user?.S3Id ? `full/${userInfo.user.S3Id}/` : undefined;
    const thumbPrefix = userInfo?.user?.S3Id ? `thumb/${userInfo.user.S3Id}/` : undefined;
    const bannerImageUrl = normalizeImageUrl(detail.bannerImage);
    const qrImageUrl = normalizeImageUrl(detail.qrImage);
    const mapImageUrl = normalizeImageUrl(detail.mapUrl);
    // Gate on the section the tree editor says this entry lives under. Entries
    // opened without that context (e.g. a direct /ts/detail/:id link, which
    // re-fetches by id and has no tab/section info) still show the field if
    // they already carry categories, so existing tags stay visible/editable.
    const showEventCategories = isEventsSectionName(detail.sectionName)
        || (Array.isArray(detail.eventCategories) && detail.eventCategories.length > 0);

    useEffect(() => {
        setShowBanner((detail.searchTags || '').includes('logo'));
    }, [detail.searchTags]);

    const carouselImageUp = (index, name) => {
        if (index > 0) {
            setDetail((prevDetail) => {
                const arr = [...(prevDetail[name] || [])];
                [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
                return { ...prevDetail, [name]: arr };
            });
        }
    };
    const carouselImageDown = (index, name) => {
        setDetail((prevDetail) => {
            const arr = [...(prevDetail[name] || [])];
            if (index < arr.length - 1) {
                [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
            }
            return { ...prevDetail, [name]: arr };
        });
    };
    const removeCarouselImage = (index, name) => {
        setDetail((prevDetail) => {
            const arr = (prevDetail[name] || []).filter((_, i) => i !== index);
            return { ...prevDetail, [name]: arr };
        });
    };

    const handleEditorChange = (newContent, part) => {
        const plainText = (newContent || '').replace(/<(.|\n)*?>/g, '').trim();
        if (plainText.length > 2048) return;
        setDetail((prevDetail) => ({
            ...prevDetail,
            [part]: newContent
        }));
    };

    const buildRatingSnippet = (score, style) => {
        const stars = '*'.repeat(score) + '-'.repeat(5 - score);

        if (style === 'score') return `Guest rating: ${score}/5`;
        if (style === 'hotel') return `${score}-star hotel`;
        return `Hotel rating: ${stars}`;
    };

    const insertToShortDescription = (snippet) => {
        const current = detail.description || '';
        const next = current ? `${current}\n${snippet}` : snippet;
        const plainText = next.trim();

        if (plainText.length > 2048) {
            message.warning('Short Description exceeds 2048 characters.');
            return;
        }

        setDetail((prevDetail) => ({
            ...prevDetail,
            description: next,
        }));
    };

    const descriptionPresets = [
        {
            label: 'Family Friendly',
            value: 'Family-friendly accommodation with spacious rooms and convenient facilities.',
        },
        {
            label: 'Prime Location',
            value: 'Located in a prime area with easy access to major attractions and transport.',
        },
        {
            label: 'Check-in Info',
            value: 'Check-in from 2:00 PM, check-out by 10:00 AM.',
        },
        {
            label: 'Popular Amenities',
            value: 'Popular amenities include free Wi-Fi, parking, and daily housekeeping.',
        },
    ];

    const quillModules = buildQuillModules([
        [{ 'header': [1, 2, 3, 4, false] }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
        [{ 'align': [] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        ['clean']
    ]);

    const quillFormats = [
        ...QUILL_TEXT_FORMATS,
        'header',
        'list',
        'bullet',
        'indent',
        'align',
        'bold',
        'italic',
        'underline',
        'strike',
        'color',
        'background',
    ];


    const updateNearbyGeoField = (field, value) => {
        setDetail((prevDetail) => ({
            ...prevDetail,
            mapData: {
                ...(prevDetail.mapData || {}),
                geoLocation: {
                    ...(prevDetail.mapData?.geoLocation || {}),
                    [field]: value,
                },
            },
        }));
    };

    const lookupNearbyAddress = async () => {
        const rawAddress = detail.mapData?.geoLocation?.address || '';
        const address = rawAddress.trim();

        if (!address) {
            message.warning('Please enter an address first.');
            return;
        }

        if (!GOOGLE_GEOCODING_KEY) {
            message.error('Google Maps API key is missing.');
            return;
        }

        setIsLookingUpAddress(true);
        try {
            const response = await fetch(
                `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${GOOGLE_GEOCODING_KEY}`
            );
            if (!response.ok) {
                throw new Error('Location lookup failed.');
            }

            const data = await response.json();
            if (data.status !== 'OK' || !Array.isArray(data.results) || data.results.length === 0) {
                throw new Error(data.error_message || 'Address not found.');
            }

            const result = data.results[0];
            const location = result.geometry?.location;
            if (typeof location?.lat !== 'number' || typeof location?.lng !== 'number') {
                throw new Error('No coordinates returned for this address.');
            }

            setDetail((prevDetail) => ({
                ...prevDetail,
                mapData: {
                    ...(prevDetail.mapData || {}),
                    geoLocation: {
                        ...(prevDetail.mapData?.geoLocation || {}),
                        address: result.formatted_address || address,
                        lat: location.lat,
                        lng: location.lng,
                    },
                },
            }));
            message.success('Location found. Coordinates updated.');
        } catch (error) {
            message.error(error.message || 'Location lookup failed.');
        } finally {
            setIsLookingUpAddress(false);
        }
    };
    const checkCharacterCount = (event, part) => {
        // 校验当前编辑器的内容是否超过2048
        let html = '';
        if (part === 'leftDescription') {
            html = detail.leftDescription || '';
        } else if (part === 'rightDescription') {
            html = detail.rightDescription || '';
        }
        const plainText = html.replace(/<(.|\n)*?>/g, '').trim();
        if (plainText.length >= 2048 && event.key !== "Backspace") {
            event.preventDefault();
        }
    };

    return (
        <div className="min-h-screen bg-white pb-24">
            <div className="max-w-7xl w-[90%] mx-auto">
                <div>
                    <div className="mb-7 pb-2 border-b border-gray-200">
                        <div className="text-2xl font-bold text-gray-800 mb-2">Content Details</div>
                        <Form
                            layout="vertical"
                            form={form}
                            initialValues={detail}
                            className="space-y-6"
                        >
                            {/* Name 字段 */}
                            <Form.Item label={<span className="text-lg font-semibold">Name</span>}>
                                <Input
                                    name="name"
                                    value={detail.name || ''}
                                    onChange={e => setDetail(prev => ({ ...prev, name: e.target.value }))}
                                    maxLength={30}
                                    className="..."
                                />
                            </Form.Item>

                            <div className="mb-6 rounded-xl border border-gray-200 bg-gray-50 px-4 py-4">
                                <div className="flex items-center justify-between gap-4">
                                    <div>
                                        <div className="text-lg font-semibold text-gray-800">Pin to top</div>
                                        <div className="text-sm text-gray-500">
                                            Keep this item ahead of the shuffled items when its parent directory is set to shuffle non-pinned items.
                                        </div>
                                    </div>
                                    <Switch
                                        checked={!!detail.pinToTop}
                                        onChange={(checked) => setDetail(prev => ({ ...prev, pinToTop: checked }))}
                                    />
                                </div>
                            </div>

                            <div className="mb-6 rounded-xl border border-gray-200 bg-gray-50 px-4 py-4">
                                <div className="text-lg font-semibold text-gray-800 mb-1">Plan Your Visit tags</div>
                                <div className="text-sm text-gray-500 mb-3">
                                    Tag this place with the interests it matches so it can be suggested by the "Plan Your Visit"
                                    recommendation feature. Leave untagged if this isn't a place visitors would plan a stop at.
                                </div>
                                <Select
                                    mode="multiple"
                                    className="!w-full mb-3"
                                    value={Array.isArray(detail.interestTags) ? detail.interestTags : []}
                                    onChange={(value) => setDetail(prev => ({ ...prev, interestTags: value }))}
                                    options={RECOMMENDATION_CATEGORIES.map((category) => ({ value: category, label: category }))}
                                    placeholder="Select one or more interests"
                                />
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Estimated visit time (minutes)
                                </label>
                                <InputNumber
                                    min={0}
                                    className="!w-full"
                                    value={detail.visitDurationMinutes ?? undefined}
                                    onChange={(value) => setDetail(prev => ({ ...prev, visitDurationMinutes: value ?? null }))}
                                    placeholder="Optional - leave blank to fit any time budget"
                                />
                            </div>

                            {showEventCategories && (
                                <div className="mb-6 rounded-xl border border-gray-200 bg-gray-50 px-4 py-4">
                                    <div className="text-lg font-semibold text-gray-800 mb-1">Event categories</div>
                                    <div className="text-sm text-gray-500 mb-3">
                                        Tag this event so it shows up under the matching category filter on the touchscreen.
                                    </div>
                                    <Select
                                        mode="multiple"
                                        className="!w-full"
                                        value={Array.isArray(detail.eventCategories) ? detail.eventCategories : []}
                                        onChange={(value) => setDetail(prev => ({ ...prev, eventCategories: value }))}
                                        options={IMPORTED_EVENT_CATEGORIES.map((category) => ({ value: category, label: category }))}
                                        placeholder="Select one or more categories"
                                    />
                                </div>
                            )}

                            <Form.Item
                                label={
                                    <div className="w-full flex items-center justify-between">
                                        <span className="text-lg font-semibold">Short Description</span>
                                        {!isNearbyLocationEntry && (
                                            <button
                                                type="button"
                                                onClick={() => setInsertPanelOpen(prev => !prev)}
                                                className="ml-3 h-7 w-7 border border-blue-300 text-blue-700 bg-white hover:bg-blue-50 text-lg leading-none"
                                                title="Insert preset"
                                            >
                                                {insertPanelOpen ? '-' : '+'}
                                            </button>
                                        )}
                                    </div>
                                }
                                name="description"
                                rules={[
                                    {
                                        required: isNearbyLocationEntry || (isQuillContentEmpty(detail.leftDescription) && isQuillContentEmpty(detail.rightDescription)),
                                        message: isNearbyLocationEntry
                                            ? 'Short Description is required for Nearby entries.'
                                            : 'Short Description is required if both Left and Right Description are empty.'
                                    },
                                    {
                                        validator: (_, value) => {
                                            const plainText = (value || '').trim();
                                            if (plainText.length > 2048) {
                                                return Promise.reject(new Error('Max 2048 characters allowed'));
                                            }
                                            return Promise.resolve();
                                        }
                                    }
                                ]}
                                className="mb-8"
                            >
                                <Input.TextArea
                                    rows={4}
                                    maxLength={2048}
                                    value={detail.description || ''}
                                    onChange={e => setDetail(prev => ({ ...prev, description: e.target.value }))}
                                    placeholder={isNearbyLocationEntry ? "Enter a short location description." : "If you leave Left and Right Description empty, please provide a description."}
                                    showCount
                                />
                                <div className="text-xs text-gray-400 mt-1">
                                    <b>Note:</b> {isNearbyLocationEntry
                                        ? 'Use Short Description for the location summary shown on the touchscreen card. Only plain text will be saved and displayed.'
                                        : 'ShortDescription does not support formatting. Only plain text will be saved and displayed.'}
                                </div>
                            </Form.Item>


                            {isNearbyLocationEntry && (
                                <div className="mb-8 rounded border border-blue-200 bg-blue-50 p-4">
                                    <div className="text-lg font-semibold text-blue-900 mb-3">Nearby Location</div>
                                    <Form.Item label="Address" className="mb-4">
                                        <Input
                                            value={detail.mapData?.geoLocation?.address || ''}
                                            onChange={e => updateNearbyGeoField('address', e.target.value)}
                                            placeholder="Enter the full address, then click Find Location"
                                        />
                                    </Form.Item>
                                    <div className="flex items-center gap-3 mb-4">
                                        <button
                                            type="button"
                                            onClick={lookupNearbyAddress}
                                            disabled={isLookingUpAddress}
                                            className="px-4 py-2 text-sm bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
                                        >
                                            {isLookingUpAddress ? 'Finding...' : 'Find Location'}
                                        </button>
                                        <span className="text-sm text-gray-600">
                                            The system will convert the address into map coordinates.
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <Form.Item label="Latitude" className="mb-0">
                                            <Input
                                                value={detail.mapData?.geoLocation?.lat ?? ''}
                                                onChange={e => updateNearbyGeoField('lat', e.target.value)}
                                                placeholder="Auto-filled after address lookup"
                                            />
                                        </Form.Item>
                                        <Form.Item label="Longitude" className="mb-0">
                                            <Input
                                                value={detail.mapData?.geoLocation?.lng ?? ''}
                                                onChange={e => updateNearbyGeoField('lng', e.target.value)}
                                                placeholder="Auto-filled after address lookup"
                                            />
                                        </Form.Item>
                                    </div>
                                </div>
                            )}
                            {!isNearbyLocationEntry && insertPanelOpen && (
                                <div className="mb-6 p-4 border border-blue-200 bg-blue-50 rounded">
                                    <div className="text-sm font-semibold text-blue-900 mb-3">Insert to Short Description</div>
                                    <div className="flex flex-col md:flex-row md:items-center gap-3">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm text-gray-700">Rating</span>
                                            <select
                                                value={ratingValue}
                                                onChange={(e) => setRatingValue(Number(e.target.value))}
                                                className="border border-gray-300 bg-white px-2 py-1"
                                            >
                                                <option value={1}>1 Star</option>
                                                <option value={2}>2 Stars</option>
                                                <option value={3}>3 Stars</option>
                                                <option value={4}>4 Stars</option>
                                                <option value={5}>5 Stars</option>
                                            </select>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm text-gray-700">Style</span>
                                            <select
                                                value={ratingStyle}
                                                onChange={(e) => setRatingStyle(e.target.value)}
                                                className="border border-gray-300 bg-white px-2 py-1"
                                            >
                                                <option value="stars">Stars</option>
                                                <option value="hotel">Hotel Style</option>
                                                <option value="score">Score</option>
                                            </select>
                                        </div>
                                        <div>
                                            <button
                                                type="button"
                                                onClick={() => insertToShortDescription(buildRatingSnippet(ratingValue, ratingStyle))}
                                                className="px-3 py-1 text-sm bg-blue-500 text-white hover:bg-blue-600 transition"
                                            >
                                                Insert Rating
                                            </button>
                                        </div>
                                    </div>

                                    <div className="mt-3 flex flex-wrap gap-2">
                                        {descriptionPresets.map((preset) => (
                                            <button
                                                key={preset.label}
                                                type="button"
                                                onClick={() => insertToShortDescription(preset.value)}
                                                className="px-3 py-1 text-sm border border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
                                            >
                                                {preset.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {!isNearbyLocationEntry && (
                                <>
                                    {/* ??????? */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                                        <Form.Item
                                            label={<span className="text-lg font-semibold">Left Description</span>}
                                            name="leftDescription"
                                            getValueFromEvent={v => v}
                                            className="mb-8"
                                            rules={[
                                                {
                                                    validator: (_, value) => {
                                                        const plainText = (value || '').replace(/<(.|\n)*?>/g, '').trim();
                                                        if (plainText.length > 2048) {
                                                            return Promise.reject(new Error('Max 2048 characters allowed'));
                                                        }
                                                        return Promise.resolve();
                                                    }
                                                }
                                            ]}
                                        >
                                            <ReactQuill
                                                theme="snow"
                                                modules={quillModules}
                                                formats={quillFormats}
                                                value={detail.leftDescription || ''}
                                                onChange={v => handleEditorChange(v, 'leftDescription')}
                                                ref={reactQuillRef}
                                                className="border min-h-[150px] max-h-[200px] overflow-y-auto"
                                                onKeyDown={e => checkCharacterCount(e, 'leftDescription')}
                                            />
                                            <div className="text-right text-xs text-gray-400 mt-1">
                                                {((detail.leftDescription || '').replace(/<(.|\n)*?>/g, '').trim().length)}/2048
                                            </div>
                                        </Form.Item>
                                        <Form.Item
                                            label={<span className="text-lg font-semibold">Right Description</span>}
                                            name="rightDescription"
                                            getValueFromEvent={v => v}
                                            className="mb-8"
                                            rules={[
                                                {
                                                    validator: (_, value) => {
                                                        const plainText = (value || '').replace(/<(.|\n)*?>/g, '').trim();
                                                        if (plainText.length > 2048) {
                                                            return Promise.reject(new Error('Max 2048 characters allowed'));
                                                        }
                                                        return Promise.resolve();
                                                    }
                                                }
                                            ]}
                                        >
                                            <ReactQuill
                                                theme="snow"
                                                modules={quillModules}
                                                formats={quillFormats}
                                                value={detail.rightDescription || ''}
                                                onChange={v => handleEditorChange(v, 'rightDescription')}
                                                ref={reactQuillRef}
                                                className="border min-h-[150px] max-h-[200px] overflow-y-auto"
                                                onKeyDown={e => checkCharacterCount(e, 'rightDescription')}
                                            />
                                            <div className="text-right text-xs text-gray-400 mt-1">
                                                {((detail.rightDescription || '').replace(/<(.|\n)*?>/g, '').trim().length)}/2048
                                            </div>
                                        </Form.Item>
                                    </div>
                                </>
                            )}
                        </Form>
                    </div>
                </div>
                <div>
                    <div className="mb-4 pb-2 border-b border-gray-200">
                        <div className="text-2xl font-bold text-gray-800 mb-2">Images</div>
                        <div className={`grid grid-cols-1 ${isNearbyLocationEntry ? '' : 'lg:grid-cols-2'} gap-10 mb-8`}>
                            {/* Map Image */}
                            <div>
                                <label className="block text-lg font-semibold mb-2 text-gray-800">Map Image (Location)</label>
                                {mapImageUrl && (
                                    <div className="aspect-video w-full rounded-xl mb-2 border overflow-hidden relative group">
                                        <img
                                            src={mapImageUrl}
                                            alt="Map"
                                            className="w-full h-full object-cover"
                                        />
                                        <button
                                            className="absolute top-2 right-2 bg-white/90 hover:bg-red-100 p-2 shadow transition"
                                            onClick={() => handleImageDelete("mapUrl")}
                                            type="button"
                                            title="Delete"
                                        >
                                            <FaTrash className="text-red-500" />
                                        </button>
                                    </div>
                                )}
                                <ImageUploader
                                    token={token}
                                    ownerType="adv"
                                    ownerId={id}
                                    width={1200}
                                    height={900}
                                    value={mapImageUrl}
                                    onChange={url =>
                                        setDetail(prev => ({ ...prev, mapUrl: normalizeImageUrl(url) }))
                                    }
                                    prefix={prefix}
                                    thumbPrefix={thumbPrefix}
                                    buttonText="Select Image"
                                />
                            </div>

                            {!isNearbyLocationEntry && (
                                <div className="mb-8">
                                    <label className="block text-lg font-semibold mb-2 text-gray-800">QR Image</label>
                                    {qrImageUrl && (
                                        <div className="aspect-square w-full max-w-[16rem] rounded-xl mb-2 border overflow-hidden relative group bg-white flex items-center justify-center">
                                            <img
                                                src={qrImageUrl}
                                                alt="QR"
                                                className="w-full h-full object-contain"
                                            />
                                            <button
                                                className="absolute top-2 right-2 bg-white/90 hover:bg-red-100 p-2 shadow transition"
                                                onClick={() => handleImageDelete("qrImage")}
                                                type="button"
                                                title="Delete"
                                            >
                                                <FaTrash className="text-red-500" />
                                            </button>
                                        </div>
                                    )}
                                    <ImageUploader
                                        token={token}
                                        ownerType="adv"
                                        ownerId={id}
                                        width={512}
                                        height={512}
                                        value={qrImageUrl}
                                        onChange={url =>
                                            setDetail(prev => ({ ...prev, qrImage: normalizeImageUrl(url) }))
                                        }
                                        prefix={prefix}
                                        thumbPrefix={thumbPrefix}
                                        buttonText="Select Image"
                                    />
                                </div>
                            )}


                            {!isNearbyLocationEntry && (
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="text-lg font-semibold text-gray-800">Banner Image (Logo)</label>
                                        <div className="flex items-center gap-2">
                                            <span className="text-gray-500 text-base">Show Logo</span>
                                            <Switch
                                                checked={showBanner}
                                                onChange={(checked) => {
                                                    setShowBanner(checked);
                                                    setDetail(prev => ({ ...prev, searchTags: checked ? 'logo' : '' }));
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {showBanner && (
                                        <>
                                            {bannerImageUrl && (
                                                <div className="w-full rounded-xl mb-2 border overflow-hidden relative group min-h-[180px] flex items-center justify-center bg-white">
                                                    <img
                                                        src={bannerImageUrl}
                                                        alt="Banner"
                                                        className="w-full h-full object-contain max-h-[220px]"
                                                    />
                                                    <button
                                                        className="absolute top-2 right-2 bg-white/90 hover:bg-red-100 p-2 shadow transition"
                                                        onClick={() => handleImageDelete("bannerImage")}
                                                        type="button"
                                                        title="Delete"
                                                    >
                                                        <FaTrash className="text-red-500" />
                                                    </button>
                                                </div>
                                            )}

                                            <ImageUploader
                                                token={token}
                                                ownerType="adv"
                                                ownerId={id}
                                                width={1200}
                                                height={900}
                                                value={bannerImageUrl}
                                                onChange={url =>
                                                    setDetail(prev => ({ ...prev, bannerImage: normalizeImageUrl(url) }))
                                                }
                                                prefix={prefix}
                                                thumbPrefix={thumbPrefix}
                                                buttonText="Select Image"
                                            />
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                        {!isNearbyLocationEntry && (
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-lg font-semibold text-gray-800">
                                        Carousel Images <span className="font-normal text-gray-500">(Max 6)</span>
                                    </span>
                                </div>
                                {((detail.imageUrls?.length || 0) < 6) && (
                                    <ImageUploader
                                        token={token}
                                        ownerType="adv"
                                        ownerId={id}
                                        width={960}
                                        height={540}
                                        prefix={prefix}
                                        thumbPrefix={thumbPrefix}
                                        buttonText="Add Carousel Image"
                                        onChange={url =>
                                            setDetail(prev => ({
                                                ...prev,
                                                imageUrls: [...(prev.imageUrls || []), url]
                                            }))
                                        }
                                    />
                                )}
                                <div className="text-gray-400 text-sm mt-2 mb-2 ml-1">
                                    Upload up to 6 images. Recommended size: 960?540px.
                                </div>
                                <div className="carousel-thumbnails mt-2 space-y-4">
                                    {(detail.imageUrls || []).map((url, index) => (
                                        <div
                                            key={url}
                                            className="grid grid-cols-[1fr_8rem_8rem] items-center bg-white border border-gray-200 rounded-xl shadow px-4 py-3 gap-4 transition-all"
                                        >
                                            <div className="aspect-[96/54] w-[30rem] bg-gray-50 overflow-hidden flex items-center justify-center col-span-1">
                                                <img src={url} alt={`Carousel ${index}`} className="w-full h-full object-contain" />
                                            </div>
                                            <div className="flex flex-col items-center justify-center space-y-2 col-span-1">
                                                <button
                                                    disabled={index === 0}
                                                    onClick={() => carouselImageUp(index, "imageUrls")}
                                                    className="text-md border bg-blue-500 text-white px-6 py-2 hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed transition w-full"
                                                    title="Move Up"
                                                    type="button"
                                                >
                                                    Up
                                                </button>
                                                <button
                                                    disabled={index === (detail.imageUrls?.length || 0) - 1}
                                                    onClick={() => carouselImageDown(index, "imageUrls")}
                                                    className="text-md border bg-blue-500 text-white px-6 py-2 hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed transition w-full"
                                                    title="Move Down"
                                                    type="button"
                                                >
                                                    Down
                                                </button>
                                            </div>
                                            <div className="flex items-center justify-center col-span-1">
                                                <button
                                                    onClick={() => removeCarouselImage(index, "imageUrls")}
                                                    className="rounded-full p-3 text-gray-400 hover:text-white hover:bg-red-500 focus:outline-none transition"
                                                    title="Delete"
                                                    type="button"
                                                >
                                                    <FaTrash className="text-2xl" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TsLeafNode;





